/**
 * Push the printers configured in the dashboard (this PC's devices plus the
 * global registry) to the local print agent, so the agent's "Print to another
 * PC" list shows every configured printer with an Allow toggle.
 *
 * Both printers on OTHER PCs and this PC's own printers are included: local ones
 * are tagged `local` so the agent can show them as "This PC" without an Allow
 * toggle (local prints never consult the remote allow-list). The agent never
 * discovers these itself — it only learns about them when the dashboard sends
 * them. This util is the single source for that build+push, used at app start
 * (MainLayout) and on the Devices page.
 */
import { ConnectedDevice, PRINTER_DEVICE_TYPES, getDevices } from './devices';
import { settingsService, DevicePayload } from '../services/httpServices/settingsService';
import {
  agentSyncPrinters,
  getPrintAgentIdentities,
  isPrintAgentAvailable,
  type AgentRemotePrinter,
} from './printAgent';

/** Always keep the dashboard's own origin in the list so it can't lock itself out. */
export function ensureCurrentOrigin(origins: string[]): string[] {
  const list = [...origins];
  try {
    const origin = (window.location?.origin || '').replace(/\/+$/, '');
    if (origin && !list.some((o) => o.replace(/\/+$/, '').toLowerCase() === origin.toLowerCase())) {
      list.push(origin);
    }
  } catch {
    /* non-browser context */
  }
  return list;
}

// Central allow-list, fetched from the backend and cached briefly so the 30s
// printer sync does not hit the settings endpoint on every tick.
let cachedOrigins: string[] | null = null;
let originsFetchedAt = 0;
const ORIGINS_TTL_MS = 5 * 60 * 1000;

/** Dashboard origins an admin configured for every workstation's agent. */
export async function getCentralAllowedOrigins(): Promise<string[]> {
  const now = Date.now();
  if (cachedOrigins !== null && now - originsFetchedAt < ORIGINS_TTL_MS) return cachedOrigins;
  try {
    const res = await settingsService.getAllowedAgentOrigins();
    cachedOrigins = (res.data?.origins || []).filter(Boolean);
    originsFetchedAt = now;
  } catch {
    /* backend unavailable — keep the last known list */
  }
  return cachedOrigins ?? [];
}

/** Drop the cached central allow-list (call after saving a new one). */
export function invalidateCentralAllowedOrigins(): void {
  cachedOrigins = null;
  originsFetchedAt = 0;
}

/** True when a host string names this workstation (so it is a local printer). */
function isLocalHost(
  host: string,
  localHostname: string,
  localIps: string[],
): boolean {
  const lower = (host || '').trim().toLowerCase();
  if (!lower || lower === 'localhost' || lower === '127.0.0.1' || lower === '::1' || lower === '.') {
    return true;
  }
  if (localHostname && lower === localHostname.trim().toLowerCase()) return true;
  return localIps.some((ip) => (ip || '').trim().toLowerCase() === lower);
}

/**
 * Build the list of configured printers (this PC's plus other PCs') to push to
 * the local agent. Printers on this PC are marked `local` and canonicalised to
 * `\\<this-pc>\<share>`; printers on other PCs keep `\\<host>\<share>`.
 */
export function buildRemotePrinters(
  localHostname: string,
  localDevices: ConnectedDevice[],
  registryDevices: DevicePayload[],
  localIps: string[] = [],
): AgentRemotePrinter[] {
  const out = new Map<string, AgentRemotePrinter>();
  const self = (localHostname || 'localhost').trim();

  const add = (name: string, host: string, share: string) => {
    const s = (share || '').trim();
    if (!s) return;
    const local = isLocalHost(host, localHostname, localIps);
    // A local device may carry this PC's IP, its hostname, or no host at all;
    // canonicalise all of them to the hostname so the row is deduplicated.
    const target = local ? `\\\\${self}\\${s}` : `\\\\${(host || '').trim()}\\${s}`;
    const key = local ? `local\\${s.toLowerCase()}` : target.toLowerCase();
    out.set(key, { name: name || s, target, local });
  };

  for (const d of localDevices) {
    if (!PRINTER_DEVICE_TYPES.includes(d.type)) continue;
    add(d.name, d.networkIp || d.pcName || d.workstation, d.shareName);
  }
  for (const d of registryDevices) {
    if (!d.shareName || !PRINTER_DEVICE_TYPES.includes(d.type as ConnectedDevice['type'])) continue;
    add(d.name, d.networkIp || d.pcName || d.workstation || '', d.shareName);
  }

  return [...out.values()];
}

let inFlight: Promise<number> | null = null;
// Last list successfully pushed, so the periodic sync does not re-send (and the
// agent does not rewrite its settings/log) when nothing changed. `null` means
// nothing has been pushed yet, so even an empty list is sent once to clear the
// agent's "waiting for the dashboard" hint.
let lastSyncedKey: string | null = null;

/**
 * Read this PC's devices plus the global registry and push the resulting
 * other-PC printer list to the local agent. Safe to call frequently or
 * concurrently (deduplicated). Resolves to the number of printers pushed, or 0
 * when no agent is reachable. Never throws.
 */
export async function syncRemotePrintersToAgent(): Promise<number> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      if (!(await isPrintAgentAvailable())) return 0;
      const identity = await getPrintAgentIdentities();
      const localDevices = getDevices();

      let registryDevices: DevicePayload[] = [];
      try {
        registryDevices = (await settingsService.listDevices()).data || [];
      } catch {
        /* registry unavailable — sync what we have locally */
      }

      const printers = buildRemotePrinters(
        identity.hostname,
        localDevices,
        registryDevices,
        identity.ipAddresses,
      );
      const origins = ensureCurrentOrigin(await getCentralAllowedOrigins());
      const key =
        printers
          .map((p) => p.target.toLowerCase())
          .sort()
          .join('|') +
        '||' +
        origins.join(',');
      if (lastSyncedKey !== null && key === lastSyncedKey) return printers.length;
      await agentSyncPrinters(printers, origins.length > 0 ? origins : undefined);
      lastSyncedKey = key;
      return printers.length;
    } catch {
      /* agent offline / older version — ignore */
      return 0;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

let syncTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Keep the local agent's "Print to another PC" list current while the dashboard
 * is open. Pushes immediately, then re-pushes on an interval so printers that
 * another PC registers (or hosts that get filled in later) appear without a
 * reload — and so a workstation whose agent starts after login is picked up.
 * Safe to call repeatedly; call {@link stopRemotePrinterSync} on logout.
 */
export function startRemotePrinterSync(intervalMs = 30_000): void {
  if (syncTimer) return;
  void syncRemotePrintersToAgent();
  syncTimer = setInterval(() => void syncRemotePrintersToAgent(), intervalMs);
}

/** Stop the periodic remote-printer sync started by {@link startRemotePrinterSync}. */
export function stopRemotePrinterSync(): void {
  if (!syncTimer) return;
  clearInterval(syncTimer);
  syncTimer = null;
}
